import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { gql } from '@apollo/client';
import { Switch, Input, Button } from '@artstorefronts/ui';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../../components/AdminLayout';
import { WELCOME_FLOW_FLAG } from '../../constants/onboarding';

const GET_FEATURE_FLAGS = gql`
  query GetFeatureFlags {
    featureFlags {
      id
      key
      name
      description
      enabled
      launchAt
    }
  }
`;

const UPDATE_FEATURE_FLAG = gql`
  mutation UpdateFeatureFlag($input: UpdateFeatureFlagInput!) {
    updateFeatureFlag(input: $input) {
      id
      key
      name
      description
      enabled
      launchAt
    }
  }
`;

interface FeatureFlag {
  id: string;
  key: string;
  name: string;
  description: string | null;
  enabled: boolean;
  launchAt: string | null;
}

// `<input type="datetime-local">` works in local time. Convert ISO -> local
// `YYYY-MM-DDTHH:mm` for the input and back to ISO for the mutation so the
// admin sees and types the launch time in their own timezone.
function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatLaunchStatus(iso: string | null, t: (k: string, opts?: any) => string): string {
  if (!iso) return t('features.launch-at-not-set');
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return t('features.launch-at-not-set');
  const formatted = date.toLocaleString();
  const now = Date.now();
  const diffMs = date.getTime() - now;
  const future = diffMs > 0;
  return future
    ? t('features.launch-at-launches-at', { date: formatted })
    : t('features.launch-at-launched-at', { date: formatted });
}

interface FeatureFlagRowProps {
  flag: FeatureFlag;
  onUpdate: (input: { key: string; enabled: boolean; launchAt?: string | null }) => Promise<void>;
}

function FeatureFlagRow({ flag, onUpdate }: FeatureFlagRowProps) {
  const { t } = useTranslation('admin');
  const [draft, setDraft] = useState<string>(toDatetimeLocalValue(flag.launchAt));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const persisted = toDatetimeLocalValue(flag.launchAt);
  const dirty = draft !== persisted;

  const handleToggle = async (checked: boolean) => {
    setError(null);
    try {
      await onUpdate({ key: flag.key, enabled: checked });
    } catch (err: any) {
      setError(err?.message || 'Error');
    }
  };

  const handleSave = async () => {
    if (!dirty) return;
    setError(null);
    setSaving(true);
    try {
      const launchAt = draft === '' ? null : new Date(draft).toISOString();
      await onUpdate({ key: flag.key, enabled: flag.enabled, launchAt });
    } catch (err: any) {
      setError(err?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setError(null);
    setSaving(true);
    try {
      await onUpdate({ key: flag.key, enabled: flag.enabled, launchAt: null });
      setDraft('');
    } catch (err: any) {
      setError(err?.message || 'Error');
    } finally {
      setSaving(false);
    }
  };

  // `launchAt` is stored on every feature flag row but only consumed by the
  // welcome flow's OnboardingRedirect (post-launch user gating). Hide the
  // input + welcome-wizard help text on every other flag so admins don't
  // write meaningless data that nothing reads. Column stays so other flags
  // can opt in later by adding their key here.
  const showLaunchAt = flag.key === WELCOME_FLOW_FLAG;

  return (
    <div className="p-4 bg-white rounded-lg shadow space-y-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-medium">{flag.name}</h3>
          <p className="text-xs text-gray-400 font-mono">{flag.key}</p>
          {flag.description && <p className="text-sm text-gray-500 mt-1">{flag.description}</p>}
        </div>
        <Switch checked={flag.enabled} onCheckedChange={handleToggle} />
      </div>

      {showLaunchAt && (
        <div className="border-t pt-3">
          <label className="text-sm font-medium block mb-1">{t('features.launch-at-label')}</label>
          <p className="text-xs text-gray-500 mb-2">{t('features.launch-at-help')}</p>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="datetime-local"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={saving}
              className="w-auto"
            />
            <Button type="button" onClick={handleSave} disabled={!dirty || saving}>
              {t('features.launch-at-save')}
            </Button>
            <Button type="button" variant="outline" onClick={handleClear} disabled={saving || !flag.launchAt}>
              {t('features.launch-at-clear')}
            </Button>
            <span className="text-xs text-gray-500 ml-auto">{formatLaunchStatus(flag.launchAt, t)}</span>
          </div>
          {error && <p className="text-xs text-red-600 mt-2">{error}</p>}
        </div>
      )}
    </div>
  );
}

function FeaturesPage() {
  const { t } = useTranslation('admin');
  const { data: featureFlagsData, loading: featureFlagsLoading } = useQuery(GET_FEATURE_FLAGS);
  const [updateFeatureFlag] = useMutation(UPDATE_FEATURE_FLAG);

  const handleUpdate = async (input: { key: string; enabled: boolean; launchAt?: string | null }) => {
    await updateFeatureFlag({ variables: { input } });
  };

  return (
    <AdminLayout title={t('features.page-title')} description={t('features.page-description')}>
      <div className="mb-8">
        <h2 className="text-xl font-semibold mb-4">{t('features.section-heading')}</h2>
        {featureFlagsLoading ? (
          <div>{t('features.loading')}</div>
        ) : (
          <div className="grid gap-4">
            {featureFlagsData?.featureFlags.map((flag: FeatureFlag) => (
              <FeatureFlagRow key={flag.id} flag={flag} onUpdate={handleUpdate} />
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default FeaturesPage;
